import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  APP_ICONS,
  AUTO_STATUS,
  COLOR_SCHEMES,
  DEFAULT_WORKING_HOURS,
  SCHEME_GROUP_LABELS,
  formatFileSize,
  HOLD_MUSIC_CREDITS,
  HOLD_MUSIC_LABELS,
  holdMusicOptions,
  RINGBACK_LABELS,
  ringbacks,
  RINGTONE_LABELS,
  ringtones,
  SOUND_LABELS,
  WORK_DAY_LABELS,
  appIcons,
  colorSchemes,
  describeUserAgent,
  describeWorkingHours,
  formatMinute,
  notificationSounds,
  type HoldMusic,
  type Ringback,
  type Ringtone,
  workDayModes,
  type AppIcon,
  type ColorScheme,
  type NotificationPrefs,
  type NotificationSound,
  type PublicUser,
  type ThemeMode,
  type WorkDayMode,
  type WorkingHours,
} from '@avslack/shared';
import {
  useDesktopNotifications,
  useNotificationPrefs,
  useUpdateNotificationPrefs,
} from '../data/notifications';
import {
  getDesktopRelease,
  listMySessions,
  revokeMyOtherSessions,
  revokeMySession,
  unlinkProjectflow,
} from '../lib/api';
import { startProjectflowLink, useProjectflowStatus } from '../data/projectflow';
import { startOutlookLink, syncOutlookNow, unlinkOutlook, useOutlookStatus } from '../data/outlook';
import { usePushSubscription } from '../data/push';
import { deniedHelp } from '../lib/notify-platform';
import { useUpdateProfile } from '../data/workspace';
import { useUsers } from '../data/users';
import { Avatar } from './Avatar';
import {
  playNotificationSound,
  setSoundVolume,
  startHoldMusic,
  startRingback,
  startRingtone,
  stopHoldMusic,
  stopRingback,
  stopRingtone,
} from '../lib/sound';
import { useTheme } from '../lib/theme';
import { useAppIcon } from '../lib/app-icon';
import { ServiceSection } from './ServiceSettings';
import { apiBase, isDesktop } from '../lib/runtime';
import { getAutostart, setAutostart as setAutostartEnabled } from '../lib/desktop';
import { Dialog, useAnimatedClose } from './Dialog';
import { useConfirm } from './Confirm';
import { LogoTile } from './Logo';
import { Icon, type IconName } from './Icon';
import { ChannelListSettings } from './ChannelListSettings';
import { McpAccessSection } from './McpAccessSection';
import { InstallSection } from './InstallApp';
import { formatDay } from '../lib/format';

/**
 * Beallitasok.
 *
 * Egy dialogus, FULEKRE bontva: egy hosszu gorgetett lista mar tobb mint
 * husz szakasz volt, es a keresett beallitas mindig "valahol lent" ult. A fulek
 * sorrendje szandekos: elol az, amit a legtobben megvaltoztatnak (ertesites,
 * elerhetoseg), hatul az, amit egyszer allitanak be (alkalmazas, fiok).
 *
 * A Mentes gomb KOZOS: a piszkozat a fulvaltast tuleli, es egyszerre ment
 * minden ful valtozasat. A ful pottye jelzi, hol van el nem mentett modositas.
 * Ami azonnal hat (megjelenes, csatornalista, eszkozok, kapcsolatok), az nem
 * resze a piszkozatnak.
 */

const DAY_NAMES = ['V', 'H', 'K', 'Sze', 'Cs', 'P', 'Szo'];

type SettingsTab =
  'ertesitesek' | 'elerhetoseg' | 'hangok' | 'csatornak' | 'megjelenes' | 'alkalmazas' | 'fiok';

const TABS: { key: SettingsTab; label: string; icon: IconName }[] = [
  { key: 'ertesitesek', label: 'Értesítések', icon: 'ertesites' },
  { key: 'elerhetoseg', label: 'Elérhetőség', icon: 'ora' },
  { key: 'hangok', label: 'Hangok', icon: 'hang' },
  { key: 'csatornak', label: 'Csatornák', icon: 'csatorna' },
  { key: 'megjelenes', label: 'Megjelenés', icon: 'paletta' },
  { key: 'alkalmazas', label: 'Alkalmazás', icon: 'kepernyo' },
  { key: 'fiok', label: 'Fiók és kapcsolatok', icon: 'fiok' },
];

/** Melyik piszkozat-mezo melyik fulon el - a "nincs mentve" potty ebbol jon. */
const PREF_TABS: Record<keyof NotificationPrefs, SettingsTab | null> = {
  globalLevel: 'ertesitesek',
  afterHoursLevel: 'ertesitesek',
  directAlways: 'ertesitesek',
  vipUserIds: 'ertesitesek',
  keywords: 'ertesitesek',
  soundEnabled: 'hangok',
  soundName: 'hangok',
  soundVolume: 'hangok',
  ringtone: 'hangok',
  ringback: 'hangok',
  holdMusic: 'hangok',
};

const TAB_KEY = 'avslack.settingsTab';

function readTab(): SettingsTab {
  try {
    const stored = localStorage.getItem(TAB_KEY);
    if (TABS.some((tab) => tab.key === stored)) return stored as SettingsTab;
  } catch {
    // Zart tarhely (privat ablak): az elso ful nyilik.
  }
  return 'ertesitesek';
}

export function SettingsDialog({ me, onClose: closeNow }: { me: PublicUser; onClose: () => void }) {
  const onClose = useAnimatedClose(closeNow);
  const [tab, setTabState] = useState<SettingsTab>(readTab);
  const setTab = (next: SettingsTab) => {
    setTabState(next);
    try {
      localStorage.setItem(TAB_KEY, next);
    } catch {
      // Csak kenyelmi: a kovetkezo nyitas az elso fulon kezd.
    }
  };
  const { data: prefs } = useNotificationPrefs();
  const update = useUpdateNotificationPrefs();
  const updateProfile = useUpdateProfile();

  const [draft, setDraft] = useState<NotificationPrefs | null>(null);
  const [hours, setHours] = useState<WorkingHours>(me.workingHours ?? DEFAULT_WORKING_HOURS);
  const [autoStatus, setAutoStatus] = useState(me.autoStatus);
  const [keyword, setKeyword] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (prefs && !draft) setDraft(prefs);
  }, [prefs, draft]);

  if (!draft) {
    return (
      <Dialog open onOpenChange={(next) => !next && onClose()} title="Beállítások" width="lg">
        <p className="py-6 text-sm text-ink-muted">Betöltés…</p>
      </Dialog>
    );
  }

  const patch = (values: Partial<NotificationPrefs>) =>
    setDraft((current) => (current ? { ...current, ...values } : current));

  /*
    A MEGHALLGATAS azt a hangerot hasznalja, amit epp beallitottunk.

    A csuszka a PISZKOZATOT allitja, a lejatszo viszont a mentett beallitasbol
    dolgozik (lasd `useSoundVolume`) - igy a meghallgatas mentes elott mindig a
    REGI hangeron szolt, barhova huztad a csuszkat. Egy hangero-allitonal ez a
    lehető legrosszabb: eppen az visszajelzes hianyzik, amiert az ember huzza.

    Ezert a csuszka azonnal ervenybe lepteti az erteket. Amit mentes nelkul
    allitottunk, azt a bezaras visszavonja - lasd `close`.
  */
  const previewVolume = (volume: number) => {
    patch({ soundVolume: volume });
    setSoundVolume(volume);
  };

  /**
   * Bezaras MENTES NELKUL.
   *
   * A meghallgatashoz allitott hangero nem maradhat ervenyben: a piszkozat
   * tobbi mezoje sem marad meg, es egy elvetett beallitas ne szoljon tovabb.
   */
  const close = () => {
    if (prefs) setSoundVolume(prefs.soundVolume);
    onClose();
  };

  const save = async () => {
    setSaveError(null);
    try {
      await update.mutateAsync(draft);
      await updateProfile.mutateAsync({ workingHours: hours, autoStatus });
      onClose();
    } catch {
      // A dialogus NYITVA marad: a piszkozat megvan, ujra lehet probalni.
      setSaveError('Nem sikerült menteni. Próbáld újra.');
    }
  };

  /*
    Melyik fulon van EL NEM MENTETT valtozas. A piszkozat a fulvaltast
    tuleli, ezert jelezni kell, hol var meg valami a Mentesre.
  */
  const dirtyTabs = new Set<SettingsTab>();
  if (prefs) {
    for (const key of Object.keys(PREF_TABS) as (keyof NotificationPrefs)[]) {
      const owner = PREF_TABS[key];
      if (owner && JSON.stringify(draft[key]) !== JSON.stringify(prefs[key])) dirtyTabs.add(owner);
    }
  }
  if (
    JSON.stringify(hours) !== JSON.stringify(me.workingHours ?? DEFAULT_WORKING_HOURS) ||
    JSON.stringify(autoStatus) !== JSON.stringify(me.autoStatus)
  ) {
    dirtyTabs.add('elerhetoseg');
  }

  return (
    <Dialog open onOpenChange={(next) => !next && close()} title="Beállítások" width="lg">
      <SettingsTabs active={tab} dirty={dirtyTabs} onSelect={setTab} />

      <div
        role="tabpanel"
        id={`beallitas-panel-${tab}`}
        aria-labelledby={`beallitas-ful-${tab}`}
        className="h-[max(16rem,calc(var(--app-height,100dvh)*0.7-10rem))] space-y-5 overflow-y-auto pr-1 pt-4 [&>section:first-child]:border-t-0 [&>section:first-child]:pt-0"
      >
        {tab === 'ertesitesek' && (
          <>
            <LevelSection
              title="Munkaidőben értesíts"
              name="notification-level"
              value={draft.globalLevel}
              onChange={(globalLevel) => patch({ globalLevel })}
              options={[
                ['all', 'Minden üzenetről', 'Minden csatornában, ahol tag vagy.'],
                ['mentions', 'Csak említésnél és DM-nél', 'Ez a legtöbb embernek jó.'],
                ['none', 'Semmiről', 'Csak a felületen látod az olvasatlant.'],
              ]}
            />

            <LevelSection
              title="Munkaidőn kívül értesíts"
              name="after-hours-level"
              bordered
              hint={
                <>
                  A munkaidődet az{' '}
                  <button
                    type="button"
                    onClick={() => setTab('elerhetoseg')}
                    className="font-semibold text-accent hover:underline"
                  >
                    Elérhetőség
                  </button>{' '}
                  fülön állíthatod be.
                </>
              }
              value={draft.afterHoursLevel}
              onChange={(afterHoursLevel) => patch({ afterHoursLevel })}
              options={[
                [
                  'none',
                  'Semmiről',
                  'Este és hétvégén csend — kivéve, amit alább „mindig”-re állítasz.',
                ],
                ['mentions', 'Csak említésnél és DM-nél', 'Ha rád van szükség, megtudod.'],
                ['all', 'Minden üzenetről', 'Munkaidőn kívül is minden csatornából.'],
              ]}
            />

            {/* ─── Mindig-szabalyok ──────────────────────────────────────────── */}
            <section className="border-t border-border-soft pt-4">
              <h3 className="mb-1 font-display text-sm font-semibold">Mindig szóljon</h3>
              <p className="mb-2 text-xs text-ink-muted">
                Ezek munkaidőn kívül és „semmi” beállítás mellett is átjutnak. A némított
                beszélgetés kivétel: amit elhallgattattál, az hallgat. Csatornát a csatorna
                fejlécében, a harang ikonnal állíthatsz „mindig”-re.
              </p>

              <label className="flex cursor-pointer items-start gap-2.5 text-sm">
                <input
                  type="checkbox"
                  checked={draft.directAlways}
                  onChange={(event) => patch({ directAlways: event.target.checked })}
                  className="mt-1 accent-[var(--accent)]"
                />
                <span>
                  Közvetlen üzenet (DM)
                  <span className="block text-xs text-ink-muted">
                    Aki személyesen ír neked, valószínűleg tudja, hogy sürgős.
                  </span>
                </span>
              </label>

              <VipSection
                meId={me.id}
                value={draft.vipUserIds}
                onChange={(vipUserIds) => patch({ vipUserIds })}
              />
            </section>

            {/* ─── Kulcsszavak ───────────────────────────────────────────────── */}
            <section className="border-t border-border-soft pt-4">
              <h3 className="mb-1 font-display text-sm font-semibold">Kulcsszavak</h3>
              <p className="mb-2 text-xs text-ink-muted">
                Ezekre úgy értesítünk, mintha említettek volna. Ékezet-független.
              </p>

              <div className="flex flex-wrap gap-1.5">
                {draft.keywords.map((word) => (
                  <span
                    key={word}
                    className="flex items-center gap-1 rounded-pill bg-surface-sunken py-1 pl-2.5 pr-1 text-xs"
                  >
                    {word}
                    <button
                      type="button"
                      aria-label={`${word} törlése`}
                      onClick={() => patch({ keywords: draft.keywords.filter((k) => k !== word) })}
                      className="rounded-full px-1 text-ink-faint hover:text-ink"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>

              <input
                aria-label="Új kulcsszó"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter') return;
                  event.preventDefault();
                  const word = keyword.trim().toLowerCase();
                  if (word && !draft.keywords.includes(word)) {
                    patch({ keywords: [...draft.keywords, word] });
                  }
                  setKeyword('');
                }}
                placeholder="Új kulcsszó, majd Enter"
                className="mt-2 w-full rounded-control border border-border bg-surface-raised px-3 py-2 text-sm"
              />
            </section>

            <DesktopNotificationSection />
            <PushSection />
          </>
        )}

        {tab === 'elerhetoseg' && (
          <>
            <WorkingHoursSection hours={hours} onChange={setHours} timezone={me.timezone} />
            {/* ─── Automatikus statuszok ─────────────────────────────────────── */}
            <section className="border-t border-border-soft pt-4">
              <h3 className="mb-1 font-display text-sm font-semibold">Automatikus státuszok</h3>
              <p className="mb-2 text-xs text-ink-muted">
                A kézzel beállított státuszt <strong>nem írjuk felül</strong> — a saját döntésed
                többet ér, mint a kikövetkeztetett állapot.
              </p>

              <div className="space-y-1.5">
                {(
                  [
                    ['huddle', AUTO_STATUS.huddle, 'amikor ölelésben vagy'],
                    [
                      'meeting',
                      AUTO_STATUS.meeting,
                      'amikor megbeszélésen vagy (az AVSlack- vagy az Outlook-naptárad szerint)',
                    ],
                    ['focus', AUTO_STATUS.focus, 'amikor fókusz módot kapcsolsz'],
                    ['afterHours', AUTO_STATUS.after_hours, 'a munkaidőn kívül'],
                  ] as const
                ).map(([key, status, hint]) => (
                  <label key={key} className="flex cursor-pointer items-center gap-2.5 text-sm">
                    <input
                      type="checkbox"
                      checked={autoStatus[key]}
                      onChange={(event) =>
                        setAutoStatus((current) => ({ ...current, [key]: event.target.checked }))
                      }
                      className="accent-[var(--accent)]"
                    />
                    <span>
                      <span aria-hidden className="mr-1">
                        {status.emoji}
                      </span>
                      <strong className="font-medium">{status.text}</strong>
                      <span className="text-ink-muted"> — {hint}</span>
                    </span>
                  </label>
                ))}
              </div>
            </section>
          </>
        )}

        {tab === 'hangok' && (
          <>
            {/* ─── Hang ──────────────────────────────────────────────────────── */}
            <SoundSection
              enabled={draft.soundEnabled}
              selected={draft.soundName}
              volume={draft.soundVolume}
              onToggle={(soundEnabled) => patch({ soundEnabled })}
              onSelect={(soundName) => patch({ soundName })}
              onVolume={previewVolume}
            />

            {/* ─── Csengohang ────────────────────────────────────────────────── */}
            <RingtoneSection
              ringtone={draft.ringtone}
              ringback={draft.ringback}
              holdMusic={draft.holdMusic}
              onRingtone={(ringtone) => patch({ ringtone })}
              onRingback={(ringback) => patch({ ringback })}
              onHoldMusic={(holdMusic) => patch({ holdMusic })}
            />
          </>
        )}

        {tab === 'csatornak' && <ChannelListSettings onOpenChannel={close} />}

        {tab === 'megjelenes' && (
          <>
            <AppearanceSection />
            <AppIconSection />
          </>
        )}

        {tab === 'alkalmazas' && (
          <>
            <InstallSection />
            <DesktopDownloadSection />
            <DesktopAppSection />
            <ServiceSection />
          </>
        )}

        {tab === 'fiok' && (
          <>
            <OutlookSection />
            <ProjectflowSection />
            {/* Szemelyes MI-hozzaferes (versenyaudit M6) - azonnal hat, nem a Mentes resze. */}
            <McpAccessSection />
            <SessionsSection />
          </>
        )}
      </div>

      <div className="mt-4 flex items-center justify-end gap-2 border-t border-border-soft pt-4">
        {saveError && (
          <p role="alert" className="mr-auto text-xs text-danger">
            {saveError}
          </p>
        )}
        <button
          type="button"
          onClick={close}
          className="rounded-control px-4 py-2 text-sm font-medium text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink"
        >
          Mégse
        </button>
        <button
          type="button"
          onClick={() => void save()}
          disabled={update.isPending || updateProfile.isPending}
          className="rounded-control bg-accent px-4 py-2 text-sm font-semibold text-accent-ink transition-colors hover:bg-accent-hover disabled:opacity-40"
        >
          {update.isPending || updateProfile.isPending ? 'Mentés…' : 'Mentés'}
        </button>
      </div>
    </Dialog>
  );
}

// ─── Fulek ─────────────────────────────────────────────────────────────────────

/**
 * A fulsor: kis, kerekitett "jelveny" gombok, tordelve (telefonon ket sorba
 * fer, nem kell oldalra gorgetni). A potty az el nem mentett valtozast jelzi.
 * Billentyuzettel a nyilak lepnek (WAI-ARIA tabs minta).
 */
function SettingsTabs({
  active,
  dirty,
  onSelect,
}: {
  active: SettingsTab;
  dirty: Set<SettingsTab>;
  onSelect: (tab: SettingsTab) => void;
}) {
  const listRef = useRef<HTMLDivElement>(null);

  const step = (delta: number) => {
    const index = TABS.findIndex((tab) => tab.key === active);
    const next = TABS[(index + delta + TABS.length) % TABS.length]!;
    onSelect(next.key);
    listRef.current?.querySelector<HTMLButtonElement>(`#beallitas-ful-${next.key}`)?.focus();
  };

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label="Beállítások témakörei"
      className="-mx-1 flex flex-wrap gap-1.5 border-b border-border-soft px-1 pb-3"
      onKeyDown={(event) => {
        if (event.key === 'ArrowRight') step(1);
        else if (event.key === 'ArrowLeft') step(-1);
        else return;
        event.preventDefault();
      }}
    >
      {TABS.map((tab) => {
        const selected = tab.key === active;
        return (
          <button
            key={tab.key}
            id={`beallitas-ful-${tab.key}`}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-controls={`beallitas-panel-${tab.key}`}
            tabIndex={selected ? 0 : -1}
            onClick={() => onSelect(tab.key)}
            className={[
              'relative flex items-center gap-1.5 rounded-pill border px-2.5 py-1 text-xs font-semibold transition-colors duration-(--duration-quick)',
              selected
                ? 'border-accent bg-accent text-accent-ink'
                : 'border-border bg-surface text-ink-muted hover:border-accent/50 hover:text-ink',
            ].join(' ')}
          >
            <Icon name={tab.icon} size="sm" />
            {tab.label}
            {dirty.has(tab.key) && (
              <span
                aria-label="nincs mentve"
                title="Nem mentett változás"
                className={[
                  'size-1.5 rounded-full',
                  selected ? 'bg-accent-ink' : 'bg-[var(--alert)]',
                ].join(' ')}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

// ─── Ertesitesi szintek ───────────────────────────────────────────────────────

type GlobalLevel = NotificationPrefs['globalLevel'];

/** Egy szint-valaszto: munkaidoben es munkaidon kivul ugyanaz a harom lehetoseg. */
function LevelSection({
  title,
  name,
  value,
  options,
  onChange,
  bordered = false,
  hint,
}: {
  title: string;
  name: string;
  value: GlobalLevel;
  options: [GlobalLevel, string, string][];
  onChange: (value: GlobalLevel) => void;
  bordered?: boolean;
  hint?: ReactNode;
}) {
  return (
    <section className={bordered ? 'border-t border-border-soft pt-4' : undefined}>
      <h3 className="mb-2 font-display text-sm font-semibold">{title}</h3>
      {hint && <p className="-mt-1 mb-2 text-xs text-ink-muted">{hint}</p>}

      <div className="space-y-1.5">
        {options.map(([option, label, hint]) => (
          <label key={option} className="flex cursor-pointer items-start gap-2.5 text-sm">
            <input
              type="radio"
              name={name}
              checked={value === option}
              onChange={() => onChange(option)}
              className="mt-1 accent-[var(--accent)]"
            />
            <span>
              {label}
              <span className="block text-xs text-ink-muted">{hint}</span>
            </span>
          </label>
        ))}
      </div>
    </section>
  );
}

/** Ekezet-fuggetlen nev-egyezes a kollega-kereseshez. */
const foldName = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');

/**
 * VIP kollegak: amit ok irnak, arrol mindig szolunk.
 *
 * Kereses + lista, nem egy hosszu jelolonegyzet-sor: egy 2000 fos cegben a
 * teljes nevsorbol senki nem valogat, viszont a nev elso betuibol igen.
 */
function VipSection({
  meId,
  value,
  onChange,
}: {
  meId: string;
  value: string[];
  onChange: (value: string[]) => void;
}) {
  const { users, usersById, nameOf } = useUsers();
  const [query, setQuery] = useState('');

  const needle = foldName(query.trim());
  const hits = needle
    ? users
        .filter(
          (user) =>
            user.id !== meId &&
            user.isActive &&
            !user.isBot &&
            !value.includes(user.id) &&
            foldName(nameOf(user)).includes(needle),
        )
        .slice(0, 6)
    : [];

  const add = (userId: string) => {
    onChange([...value, userId]);
    setQuery('');
  };

  return (
    <div className="mt-3">
      <p className="text-sm">VIP kollégák</p>
      <p className="mb-2 text-xs text-ink-muted">
        Mindenről szólunk, amit írnak: DM, említés, és a közös csatornákba írt üzeneteik is.
      </p>

      {value.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {value.map((userId) => {
            const user = usersById.get(userId);
            const name = nameOf(userId, 'Törölt felhasználó');
            return (
              <span
                key={userId}
                className="flex items-center gap-1.5 rounded-pill bg-surface-sunken py-0.5 pl-0.5 pr-1 text-xs"
              >
                <Avatar userId={userId} name={name} avatarUrl={user?.avatarUrl} size="sm" />
                {name}
                <button
                  type="button"
                  aria-label={`${name} eltávolítása a VIP-k közül`}
                  onClick={() => onChange(value.filter((id) => id !== userId))}
                  className="rounded-full px-1 text-ink-faint hover:text-ink"
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      )}

      <input
        aria-label="VIP kolléga keresése"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          const first = hits[0];
          if (first) add(first.id);
        }}
        placeholder="Kolléga neve…"
        className="w-full rounded-control border border-border bg-surface-raised px-3 py-2 text-sm"
      />

      {hits.length > 0 && (
        <ul className="mt-1 overflow-hidden rounded-control border border-border-soft bg-surface-raised">
          {hits.map((user) => (
            <li key={user.id}>
              <button
                type="button"
                onClick={() => add(user.id)}
                className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-sm transition-colors duration-(--duration-quick) hover:bg-surface-hover"
              >
                <Avatar userId={user.id} name={nameOf(user)} avatarUrl={user.avatarUrl} size="sm" />
                {nameOf(user)}
              </button>
            </li>
          ))}
        </ul>
      )}
      {needle && hits.length === 0 && (
        <p className="mt-1 text-xs text-ink-faint">Nincs ilyen nevű kolléga.</p>
      )}
    </div>
  );
}

// ─── Munkaido ─────────────────────────────────────────────────────────────────

/**
 * Munkaido.
 *
 * Ugyanez a sav donti el, mikor ertesitunk ES mikor mutatjuk a "munkaidon
 * kivul" statuszt. Egy helyen allithato, mert a felhasznalo is egy dolognak
 * gondolja a kettot.
 */
function WorkingHoursSection({
  hours,
  onChange,
  timezone,
}: {
  hours: WorkingHours;
  onChange: (hours: WorkingHours) => void;
  timezone: string;
}) {
  const unlimited = hours.fromMinute === hours.toMinute;

  return (
    <section className="border-t border-border-soft pt-4">
      <h3 className="mb-1 font-display text-sm font-semibold">Elérhetőség</h3>
      <p className="mb-2.5 text-xs text-ink-muted">
        Csak ebben a sávban értesítünk. A sávot a saját időzónádban értelmezzük (
        <code className="font-mono text-2xs">{timezone}</code>).
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <select
          aria-label="Mely napokon"
          value={hours.days}
          onChange={(event) => onChange({ ...hours, days: event.target.value as WorkDayMode })}
          className="rounded-control border border-border bg-surface-raised px-2.5 py-1.5 text-sm"
        >
          {workDayModes.map((mode) => (
            <option key={mode} value={mode}>
              {WORK_DAY_LABELS[mode]}
            </option>
          ))}
        </select>

        <TimeSelect
          label="Kezdés"
          value={hours.fromMinute}
          onChange={(fromMinute) => onChange({ ...hours, fromMinute })}
        />
        <span className="text-xs text-ink-muted">–</span>
        <TimeSelect
          label="Vége"
          value={hours.toMinute}
          onChange={(toMinute) => onChange({ ...hours, toMinute })}
        />
      </div>

      {hours.days === 'custom' && (
        <div className="mt-2 flex flex-wrap gap-1">
          {DAY_NAMES.map((label, day) => {
            const on = hours.customDays.includes(day);
            return (
              <button
                key={day}
                type="button"
                aria-pressed={on}
                onClick={() =>
                  onChange({
                    ...hours,
                    customDays: on
                      ? hours.customDays.filter((d) => d !== day)
                      : [...hours.customDays, day].sort((a, b) => a - b),
                  })
                }
                className={`min-w-9 rounded-control px-2 py-1 text-xs transition-colors ${
                  on
                    ? 'bg-accent text-accent-ink'
                    : 'bg-surface-sunken text-ink-muted hover:text-ink'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      <p className="mt-2 text-xs text-ink-faint">
        {unlimited
          ? `${WORK_DAY_LABELS[hours.days]}, egész nap — nincs időbeli korlát.`
          : describeWorkingHours(hours)}
      </p>
    </section>
  );
}

/** Fel oras lepesekben - percre pontos beallitas itt csak zaj lenne. */
function TimeSelect({
  value,
  onChange,
  label,
}: {
  value: number;
  onChange: (value: number) => void;
  label: string;
}) {
  return (
    <select
      aria-label={label}
      value={value}
      onChange={(event) => onChange(Number(event.target.value))}
      className="rounded-control border border-border bg-surface-raised px-2 py-1.5 text-sm tabular-nums"
    >
      {Array.from({ length: 48 }, (_unused, index) => index * 30).map((minute) => (
        <option key={minute} value={minute}>
          {formatMinute(minute)}
        </option>
      ))}
    </select>
  );
}

// ─── Hang ─────────────────────────────────────────────────────────────────────

/**
 * Hivas-jelzes es varakozo zene.
 *
 * Egy szakaszban, mert ugyanahhoz a helyzethez tartoznak: valaki hiv, vagy
 * epp mi varunk valakire. A CC BY licenc miatt a zene szerzoje itt is
 * megjelenik - a nevmegjeloles nem "szep gesztus", hanem a licenc feltetele.
 */
function RingtoneSection({
  ringtone,
  ringback,
  holdMusic,
  onRingtone,
  onRingback,
  onHoldMusic,
}: {
  ringtone: Ringtone;
  ringback: Ringback;
  holdMusic: HoldMusic;
  onRingtone: (value: Ringtone) => void;
  onRingback: (value: Ringback) => void;
  onHoldMusic: (value: HoldMusic) => void;
}) {
  const [playing, setPlaying] = useState<'csengő' | 'csörgés' | 'zene' | null>(null);

  // A meghallgatas nem szolhat orokke: par masodperc utan magatol elhallgat.
  useEffect(() => {
    if (!playing) return;
    const timer = setTimeout(() => {
      stopRingtone();
      stopRingback();
      stopHoldMusic();
      setPlaying(null);
    }, 8000);
    return () => {
      clearTimeout(timer);
      stopRingtone();
      stopRingback();
      stopHoldMusic();
    };
  }, [playing]);

  return (
    <section className="border-t border-border-soft pt-4">
      <h3 className="font-display font-semibold">Csengőhang és zene</h3>

      <p className="mb-2 mt-1 text-xs text-ink-muted">
        A csengőhang akkor szól, ha valaki behív egy ölelésbe. Kattints egy névre a meghallgatáshoz.
      </p>

      <div className="flex flex-wrap gap-1.5">
        {ringtones.map((tone) => (
          <button
            key={tone}
            type="button"
            aria-pressed={ringtone === tone}
            onClick={() => {
              onRingtone(tone);
              stopHoldMusic();
              stopRingtone();
              if (tone === 'nincs') return setPlaying(null);
              startRingtone(tone);
              setPlaying('csengő');
            }}
            className={`rounded-control px-2.5 py-1.5 text-xs transition-colors ${
              ringtone === tone
                ? 'bg-accent font-semibold text-accent-ink'
                : 'bg-surface-sunken text-ink-muted hover:text-ink'
            }`}
          >
            {RINGTONE_LABELS[tone]}
          </button>
        ))}
      </div>

      {/*
        VISSZACSENGETES - kulon a csengohangtol.

        Ket kulonbozo helyzet, ket kulonbozo hang: az egyik azt jelenti, hogy
        minket hivnak, a masik azt, hogy mi varunk valakire. Ha ugyanaz szolna,
        egyik sem mondana semmit.
      */}
      <p className="mb-2 mt-4 text-xs text-ink-muted">
        Ha te hívsz valakit, ez szól, amíg fel nem veszi. Szándékosan más, mint a csengőhang — így
        hallod, melyik irányba tart a hívás.
      </p>

      <div className="flex flex-wrap gap-1.5">
        {ringbacks.map((tone) => (
          <button
            key={tone}
            type="button"
            aria-pressed={ringback === tone}
            onClick={() => {
              onRingback(tone);
              stopRingtone();
              stopRingback();
              stopHoldMusic();
              if (tone === 'nincs') return setPlaying(null);
              if (tone === 'zene') {
                startHoldMusic(holdMusic);
                setPlaying('zene');
                return;
              }
              startRingback(tone);
              setPlaying('csörgés');
            }}
            className={`rounded-control px-2.5 py-1.5 text-xs transition-colors ${
              ringback === tone
                ? 'bg-accent font-semibold text-accent-ink'
                : 'bg-surface-sunken text-ink-muted hover:text-ink'
            }`}
          >
            {RINGBACK_LABELS[tone]}
          </button>
        ))}
      </div>

      <p className="mb-2 mt-4 text-xs text-ink-muted">
        Ha egyedül maradsz egy ölelésben, fél perc után halkan elindul a zene — így nem egy néma
        szobában vársz.
      </p>

      <div className="flex flex-wrap gap-1.5">
        {holdMusicOptions.map((track) => (
          <button
            key={track}
            type="button"
            aria-pressed={holdMusic === track}
            onClick={() => {
              onHoldMusic(track);
              stopRingtone();
              stopHoldMusic();
              if (track === 'nincs') return setPlaying(null);
              startHoldMusic(track);
              setPlaying('zene');
            }}
            className={`rounded-control px-2.5 py-1.5 text-xs transition-colors ${
              holdMusic === track
                ? 'bg-accent font-semibold text-accent-ink'
                : 'bg-surface-sunken text-ink-muted hover:text-ink'
            }`}
          >
            {HOLD_MUSIC_LABELS[track]}
          </button>
        ))}
      </div>

      {/*
        A nevmegjeloles a CC BY licenc FELTETELE, nem dekoracio: enelkul nem
        hasznalhatnank a zenet.
      */}
      {HOLD_MUSIC_CREDITS[holdMusic] && (
        <p className="mt-2 text-2xs text-ink-faint">{HOLD_MUSIC_CREDITS[holdMusic]}</p>
      )}
    </section>
  );
}

function SoundSection({
  enabled,
  selected,
  volume,
  onToggle,
  onSelect,
  onVolume,
}: {
  enabled: boolean;
  selected: NotificationSound;
  volume: number;
  onToggle: (enabled: boolean) => void;
  onSelect: (sound: NotificationSound) => void;
  onVolume: (volume: number) => void;
}) {
  return (
    <section className="border-t border-border-soft pt-4">
      <label className="flex cursor-pointer items-center gap-2.5 text-sm">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(event) => onToggle(event.target.checked)}
          className="accent-[var(--accent)]"
        />
        <span className="font-display font-semibold">Hangjelzés</span>
      </label>

      {enabled && (
        <>
          <p className="mb-2 mt-1.5 text-xs text-ink-muted">
            Kattints egy hangra a meghallgatáshoz. Mind rövid és halk — aki egész nap ezt hallja,
            annak a hangosabb változat pár nap alatt elviselhetetlen lenne.
          </p>

          <div className="flex flex-wrap gap-1.5">
            {notificationSounds.map((sound) => (
              <button
                key={sound}
                type="button"
                aria-pressed={selected === sound}
                onClick={() => {
                  onSelect(sound);
                  playNotificationSound(sound);
                }}
                className={`rounded-control px-2.5 py-1.5 text-xs transition-colors ${
                  selected === sound
                    ? 'bg-accent font-semibold text-accent-ink'
                    : 'bg-surface-sunken text-ink-muted hover:text-ink'
                }`}
              >
                {SOUND_LABELS[sound]}
              </button>
            ))}
          </div>

          {/*
            HANGERO - minden sajat hangunkra.

            Eddig nem letezett, es ez a rendszer beallitasaibol sem volt
            potolhato: az ertesitest szandekosan a sajat hangunkkal jatsszuk le
            (`silent: true` az ertesitesen), tehat a Windows ertesitesi
            hangereje nem szabalyozza. Akinek hangos volt, annak csak a teljes
            kikapcsolas maradt.

            A csuszka a csengohangra es a varakozo zenere is vonatkozik -
            SZORZOKENT: a hangok egymashoz kepesti aranya nem valtozik tole.
          */}
          <div className="mt-4 flex items-center gap-3">
            <span aria-hidden="true" className="text-ink-faint">
              <Icon name="huddle" size="sm" />
            </span>
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={volume}
              onChange={(event) => onVolume(Number(event.target.value))}
              /*
                Meghallgatas kozben allitani a legtermeszetesebb: a
                felengedeskor megszolal, amit epp beallitottunk. A
                billentyuzetes allitas (nyilak) ugyanezt kapja - kulonben az
                egeret nem hasznalo felhasznalo semmilyen visszajelzest nem
                kapna arrol, amit eppen csinal.
              */
              onPointerUp={() => playNotificationSound(selected)}
              onKeyUp={() => playNotificationSound(selected)}
              aria-label="Hangerő"
              className="h-1 min-w-0 flex-1 cursor-pointer accent-[var(--accent)]"
            />
            <span className="w-10 shrink-0 text-right text-xs tabular-nums text-ink-muted">
              {volume}%
            </span>
          </div>
          <p className="mt-1 text-xs text-ink-muted">
            A csengőhangra és a várakozó zenére is vonatkozik. A gép hangereje ezt nem szabályozza:
            az értesítést az AVSlack a saját hangjával szólaltatja meg.
          </p>
        </>
      )}
    </section>
  );
}

// ─── Megjelenes ───────────────────────────────────────────────────────────────

function AppearanceSection() {
  const { mode, scheme, setMode, setScheme } = useTheme();
  const updateProfile = useUpdateProfile();

  /** A valtas AZONNAL latszik, es a szerverre is felmegy (kovesse a gepeket). */
  const applyMode = (next: ThemeMode) => {
    setMode(next);
    updateProfile.mutate({ themeMode: next });
  };
  const applyScheme = (next: ColorScheme) => {
    setScheme(next);
    updateProfile.mutate({ colorScheme: next });
  };

  const groups = ['skandinav', 'elenk'] as const;

  return (
    <section className="border-t border-border-soft pt-4">
      <h3 className="mb-2 font-display text-sm font-semibold">Megjelenés</h3>

      <div className="flex gap-1.5">
        {(
          [
            ['light', '☀', 'Világos'],
            ['dark', '☾', 'Sötét'],
            ['system', '◐', 'Rendszer'],
          ] as const
        ).map(([value, icon, label]) => (
          <button
            key={value}
            type="button"
            aria-pressed={mode === value}
            onClick={() => applyMode(value)}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-control px-3 py-2 text-xs transition-colors ${
              mode === value
                ? 'bg-accent font-semibold text-accent-ink'
                : 'bg-surface-sunken text-ink-muted hover:text-ink'
            }`}
          >
            <span aria-hidden>{icon}</span>
            {label}
          </button>
        ))}
      </div>

      {groups.map((group) => (
        <div key={group} className="mt-3">
          <p className="mb-1.5 text-2xs font-medium uppercase tracking-wide text-ink-faint">
            {SCHEME_GROUP_LABELS[group]}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {colorSchemes
              .filter((name) => COLOR_SCHEMES[name].group === group)
              .map((name) => (
                <button
                  key={name}
                  type="button"
                  aria-pressed={scheme === name}
                  onClick={() => applyScheme(name)}
                  title={COLOR_SCHEMES[name].name}
                  className={`flex items-center gap-1.5 rounded-control py-1 pl-1 pr-2.5 text-xs transition-colors ${
                    scheme === name
                      ? 'bg-surface-hover font-semibold ring-2 ring-accent'
                      : 'bg-surface-sunken text-ink-muted hover:text-ink'
                  }`}
                >
                  <span
                    aria-hidden
                    className="size-4 rounded-full"
                    style={{ background: COLOR_SCHEMES[name].swatch }}
                  />
                  {COLOR_SCHEMES[name].name}
                </button>
              ))}
          </div>
        </div>
      ))}
    </section>
  );
}

// ─── Alkalmazas-ikon ──────────────────────────────────────────────────────────

/**
 * Az alkalmazás-ikon választója.
 *
 * Mind a hat csempe UGYANAZ A JEL, csak más színekkel - a geometria egy
 * képpontot sem tér el. A petrol azért kapott társakat, mert egy munkahelyi
 * tálcán eltűnik: nem halvány, hanem kék, és abban a színsávban ott van a
 * levelező, a böngésző, a kódszerkesztő és maga a rendszer is.
 */
function AppIconSection() {
  const { icon, setIcon } = useAppIcon();
  const updateProfile = useUpdateProfile();

  /** A váltás AZONNAL látszik, és a szerverre is felmegy (kövesse a gépeket). */
  const apply = (next: AppIcon) => {
    setIcon(next);
    updateProfile.mutate({ appIcon: next });
  };

  return (
    <section className="border-t border-border-soft pt-4">
      <h3 className="mb-0.5 font-display text-sm font-semibold">Alkalmazás-ikon</h3>
      <p className="mb-2.5 text-xs text-ink-muted">
        Ugyanaz a jel, más színekkel. A böngészőfülön és az asztali app tálcaikonján azonnal vált.
      </p>

      <div className="flex flex-wrap gap-2">
        {appIcons.map((name) => (
          <button
            key={name}
            type="button"
            onClick={() => apply(name)}
            title={APP_ICONS[name].hint}
            aria-pressed={icon === name}
            className={`flex w-[4.5rem] flex-col items-center gap-1.5 rounded-control px-1.5 py-2 transition-colors ${
              icon === name
                ? 'bg-surface-hover ring-2 ring-accent'
                : 'bg-surface-sunken hover:bg-surface-hover'
            }`}
          >
            <LogoTile
              icon={name}
              className="size-10 rounded-[0.7rem]"
              title={APP_ICONS[name].name}
            />
            <span
              className={`text-2xs ${icon === name ? 'font-semibold text-ink' : 'text-ink-muted'}`}
            >
              {APP_ICONS[name].name}
            </span>
          </button>
        ))}
      </div>

      {/*
        AZ ELVARAST ITT KELL HELYRE TENNI, nem a hibabejelentesben.

        A telepitett alkalmazas ikonjat a RENDSZER tarolja, nem mi. Androidon a
        Chrome sajat utemezese szerint frissit (akar egy-ket nap), iOS-en pedig
        a kezdokepernyore kitett ikon SOHA nem valtozik - a Safari a hozzaadas
        pillanataban lefagyasztja. Aki ezt nem tudja, hibanak fogja hinni.
      */}
      <p className="mt-2 text-2xs leading-relaxed text-ink-faint">
        A telepített alkalmazás ikonját a rendszer tárolja: Androidon a böngésző saját ütemezése
        szerint frissül, iPhone-on pedig a kezdőképernyőre kitett ikon nem változik — ott újra ki
        kell tenni. Az asztali telepítő beégetett ikonja (Start menü, kitűzött parancsikon) szintén
        marad.
      </p>
    </section>
  );
}

// ─── Rendszer-ertesites, push, desktop ────────────────────────────────────────

function DesktopNotificationSection() {
  const desktop = useDesktopNotifications(() => {});

  return (
    <section className="border-t border-border-soft pt-4 text-xs">
      <h3 className="mb-1 font-display text-sm font-semibold">Rendszer-értesítés</h3>
      <p className="text-ink-muted">Akkor jelenik meg, ha az AVSlack fut, de nincs előtérben.</p>

      {desktop.permission === 'default' && (
        <button
          type="button"
          onClick={() => void desktop.request()}
          className="mt-1.5 rounded-control bg-accent px-2.5 py-1 font-semibold text-accent-ink transition-colors hover:bg-accent-hover"
        >
          Engedélyezés kérése
        </button>
      )}
      {desktop.permission === 'denied' && (
        <p className="mt-0.5 text-ink-muted">
          A böngésző letiltotta. A címsor melletti lakat ikonnal kapcsolhatod vissza.
        </p>
      )}
      {desktop.permission === 'granted' && <p className="mt-0.5 text-ink-muted">Engedélyezve.</p>}
    </section>
  );
}

function PushSection() {
  const push = usePushSubscription();
  // A 'working' allapot nem mondja meg, MELYIK irany fut - ezt mi jegyezzuk meg.
  const [action, setAction] = useState<'enable' | 'disable' | null>(null);
  if (push.state === 'unsupported') return null;

  const busyLabel =
    action === 'disable' ? 'Kikapcsolás…' : action === 'enable' ? 'Bekapcsolás…' : 'Ellenőrzés…';

  return (
    <section className="border-t border-border-soft pt-4 text-xs">
      <h3 className="mb-1 font-display text-sm font-semibold">Értesítés zárt app mellett</h3>

      {push.state === 'unavailable' ? (
        <p className="text-ink-muted">A szerveren nincs beállítva push.</p>
      ) : push.state === 'denied' ? (
        /*
          Zsakutca volt: kozoltuk, hogy le van tiltva, de nem mondtuk meg, mit
          lehet tenni - iPhone-on pedig ez nem egy kapcsolo atallitasa.
        */
        <p className="text-ink-muted">A böngésző letiltotta az értesítést. {deniedHelp()}</p>
      ) : (
        <div className="flex items-start gap-3">
          <p className="min-w-0 flex-1 text-ink-muted">
            {push.state === 'on'
              ? 'Bekapcsolva ezen a gépen.'
              : 'Kapcsold be, ha zárt alkalmazás mellett is szeretnél értesítést.'}
          </p>
          <button
            type="button"
            disabled={push.state === 'working'}
            onClick={() => {
              const next = push.state === 'on' ? 'disable' : 'enable';
              setAction(next);
              void (next === 'disable' ? push.disable() : push.enable());
            }}
            className={`shrink-0 rounded-control px-2.5 py-1 font-semibold transition-colors disabled:opacity-50 ${
              push.state === 'on'
                ? 'bg-surface-sunken text-ink-muted hover:text-ink'
                : 'bg-accent text-accent-ink hover:bg-accent-hover'
            }`}
          >
            {push.state === 'working'
              ? busyLabel
              : push.state === 'on'
                ? 'Kikapcsolás'
                : 'Bekapcsolás'}
          </button>
        </div>
      )}
    </section>
  );
}

/**
 * A telepito letoltese - CSAK a bongeszoben.
 *
 * Aki mar az asztali alkalmazasban ul, annak nincs mit letoltenie. A fajlok a
 * privat GitHub-tarolo kiadasaibol jonnek, de nem kozvetlenul: a szerver
 * kozvetiti oket (lasd modules/desktop), igy a kollegaknak nem kell
 * GitHub-fiok.
 */
function DesktopDownloadSection() {
  const { data } = useQuery({
    queryKey: ['desktop-release'],
    queryFn: getDesktopRelease,
    staleTime: 10 * 60_000,
    enabled: !isDesktop(),
  });

  if (isDesktop() || !data) return null;

  const windows = data.release?.assets.find((asset) => asset.platform === 'windows');
  const macos = data.release?.assets.find((asset) => asset.platform === 'macos');

  return (
    <section className="border-t border-border-soft pt-4 text-xs">
      <h3 className="mb-1.5 font-display text-sm font-semibold">Asztali alkalmazás</h3>

      {!data.enabled || !data.release ? (
        <p className="text-ink-muted">
          {data.enabled
            ? 'Még nincs kiadott telepítő.'
            : 'A telepítő letöltése nincs beállítva ezen a kiszolgálón.'}
        </p>
      ) : (
        <>
          <p className="mb-2 text-ink-muted">
            A böngészőben minden működik, de az asztali alkalmazás a tálcán marad, és az ölelés
            akkor is szól, ha épp más ablakban dolgozol.
          </p>

          <div className="flex flex-wrap gap-1.5">
            {[windows, macos].filter(Boolean).map((asset) => (
              <a
                key={asset!.id}
                href={`${apiBase()}/api/desktop/download/${asset!.id}`}
                className="flex items-center gap-2 rounded-control bg-accent px-3 py-2 text-xs font-semibold text-accent-ink transition-colors hover:bg-accent-hover"
              >
                <Icon name="letoltes" size="sm" />
                {asset!.platform === 'windows' ? 'Windows' : 'macOS'}
                <span className="font-normal opacity-80">{formatFileSize(asset!.size)}</span>
              </a>
            ))}
          </div>

          <p className="mt-2 text-ink-faint">
            {data.release.version} verzió
            {data.release.publishedAt && ` · ${formatDay(data.release.publishedAt)}`}
          </p>
        </>
      )}
    </section>
  );
}

/**
 * ProjectFlow-osszekotes.
 *
 * Csak akkor jelenik meg, ha a cegnel be van kapcsolva - egy kesobb behozott
 * ceg ne talalkozzon egy olyan rendszer nevevel, amirol lehet, hogy nem is tud.
 *
 * A ProjectFlow-fiok NEVET nem irjuk ki: a masik oldal a token mellé nem ad
 * felhasznalo-adatot, es az AVSlack-fiok e-mail cimet kiirni felrevezeto lenne
 * - nem tudjuk ellenorizni, hogy tenyleg ahhoz a fiokhoz kotottuk.
 */
function ProjectflowSection() {
  const queryClient = useQueryClient();
  const { data } = useProjectflowStatus();
  const confirm = useConfirm();

  if (!data?.enabled) return null;

  return (
    <section className="border-t border-border-soft pt-4 text-xs">
      <h3 className="mb-1.5 font-display text-sm font-semibold">ProjectFlow</h3>

      {data.linked ? (
        <div className="flex flex-wrap items-center gap-2">
          <p className="flex-1 text-ink-muted">
            A fiókod össze van kötve. Egy üzenet vagy egy ölelés átirata mellől a{' '}
            <span className="font-medium text-ink">te nevedben</span> tudsz feladatot létrehozni.
          </p>
          <button
            type="button"
            onClick={async () => {
              const ok = await confirm({
                title: 'ProjectFlow szétkapcsolása',
                body: 'Utána nem tudsz feladatot létrehozni az AVSlackből, amíg újra össze nem kötöd.',
                confirmLabel: 'Szétkapcsolás',
                danger: true,
              });
              if (!ok) return;
              await unlinkProjectflow();
              await queryClient.invalidateQueries({ queryKey: ['projectflow'] });
            }}
            className="rounded-control border border-border px-2.5 py-1 text-ink-muted hover:text-ink"
          >
            Szétkapcsolás
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <p className="flex-1 text-ink-muted">
            Kösd össze a ProjectFlow-fiókodat, és egy üzenetből vagy egy átiratból egy kattintással
            lesz feladat &mdash; a saját nevedben, a saját projektjeidbe.
          </p>
          <button
            type="button"
            onClick={startProjectflowLink}
            className="rounded-control bg-accent px-2.5 py-1 font-semibold text-accent-ink"
          >
            Összekötés
          </button>
        </div>
      )}
    </section>
  );
}

/**
 * BEJELENTKEZETT ESZKOZOK (versenyaudit B2).
 *
 * Elveszett telefon, kozos gep, regi laptop: innen egy kattintassal kilep. A
 * sajat (mostani) eszkozt nem lehet innen kileptetni - arra a Kilepes valo.
 */
function SessionsSection() {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { data: sessions } = useQuery({ queryKey: ['sessions'], queryFn: listMySessions });
  const [error, setError] = useState<string | null>(null);

  if (!sessions || sessions.length === 0) return null;
  const others = sessions.filter((session) => !session.current);

  const run = async (action: () => Promise<unknown>) => {
    setError(null);
    try {
      await action();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Nem sikerült.');
    } finally {
      await queryClient.invalidateQueries({ queryKey: ['sessions'] });
    }
  };

  return (
    <section className="border-t border-border-soft pt-4 text-xs">
      <div className="mb-1.5 flex items-center gap-2">
        <h3 className="flex-1 font-display text-sm font-semibold">Bejelentkezett eszközök</h3>
        {others.length > 0 && (
          <button
            type="button"
            onClick={async () => {
              const ok = await confirm({
                title: 'Kiléptetés minden más eszközről',
                body: `${others.length} másik eszközön újra be kell majd lépned. Ez az eszköz bent marad.`,
                confirmLabel: 'Kiléptetés',
                danger: true,
              });
              if (ok) await run(revokeMyOtherSessions);
            }}
            className="rounded-control border border-border px-2.5 py-1 text-ink-muted hover:text-ink"
          >
            Minden más eszköz kiléptetése
          </button>
        )}
      </div>
      <ul className="space-y-1.5">
        {sessions.map((session) => (
          <li key={session.id} className="flex items-center gap-2">
            <span aria-hidden="true" className="text-ink-faint">
              <Icon name="kepernyo" size="sm" />
            </span>
            <span className="min-w-0 flex-1 truncate">
              <span className="font-medium text-ink">
                {describeUserAgent(session.userAgent, session.client)}
              </span>
              {session.method === 'entra' && (
                <span className="ml-1.5 text-ink-muted">· Microsoft-fiókkal</span>
              )}
              {session.current && <span className="ml-1.5 text-accent">· ez az eszköz</span>}
              <span className="ml-1.5 text-ink-muted">
                · utoljára {formatDay(session.lastSeenAt)}
                {session.ip ? ` · ${session.ip}` : ''}
              </span>
            </span>
            {!session.current && (
              <button
                type="button"
                onClick={() => void run(() => revokeMySession(session.id))}
                className="rounded-control px-2 py-0.5 text-ink-muted hover:bg-surface-hover hover:text-ink"
              >
                Kiléptetés
              </button>
            )}
          </li>
        ))}
      </ul>
      {error && (
        <p role="alert" className="mt-1.5 text-danger">
          {error}
        </p>
      )}
    </section>
  );
}

function DesktopAppSection() {
  const [autostart, setAutostart] = useState<boolean | null>(null);

  useEffect(() => {
    if (!isDesktop()) return;
    void getAutostart().then(setAutostart);
  }, []);

  if (!isDesktop() || autostart === null) return null;

  return (
    <section className="border-t border-border-soft pt-4 text-xs">
      <h3 className="mb-1.5 font-display text-sm font-semibold">Alkalmazás</h3>

      <label className="flex cursor-pointer items-start gap-2.5">
        <input
          type="checkbox"
          checked={autostart}
          onChange={(event) => {
            const next = event.target.checked;
            setAutostart(next);
            void setAutostartEnabled(next).catch(() => setAutostart(!next));
          }}
          className="mt-0.5 accent-[var(--accent)]"
        />
        <span>
          Induljon a géppel
          <span className="mt-0.5 block text-ink-muted">A tálcára indul, nem nyit ablakot.</span>
        </span>
      </label>

      <p className="mt-2 text-ink-muted">
        Az ablak bezárása a tálcára teszi az AVSlacket. Bárhonnan előhozhatod:{' '}
        <kbd className="font-mono text-2xs">Ctrl+Shift+A</kbd>.
      </p>
    </section>
  );
}

/**
 * OUTLOOK-NAPTAR (docs/23, B1).
 *
 * Osszekotes utan a sajat Outlook-megbeszelesek az AVSlack-naptarban is
 * latszanak (csak neked), "Megbeszelesen" statuszt kapsz, a kollegak pedig
 * a foglalt savjaidat latjak - cim nelkul.
 */
function OutlookSection() {
  const queryClient = useQueryClient();
  const { data } = useOutlookStatus();
  const confirm = useConfirm();
  const [syncing, setSyncing] = useState(false);

  if (!data?.enabled) return null;
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['outlook'] });

  return (
    <section className="border-t border-border-soft pt-4 text-xs">
      <h3 className="mb-1.5 font-display text-sm font-semibold">Outlook-naptár</h3>

      {!data.linked ? (
        <div className="flex flex-wrap items-center gap-2">
          <p className="flex-1 text-ink-muted">
            Kösd össze az Outlook-naptáradat: a megbeszéléseid megjelennek az AVSlack-naptáradban,
            megbeszélés közben „Megbeszélésen” státuszt kapsz, a kollégák pedig látják a foglalt
            sávjaidat &mdash; <span className="font-medium text-ink">cím nélkül</span>.
          </p>
          <button
            type="button"
            onClick={startOutlookLink}
            className="rounded-control bg-accent px-2.5 py-1 font-semibold text-accent-ink"
          >
            Összekötés
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {data.needsReauth ? (
            <p role="alert" className="text-[var(--danger)]">
              A Microsoft visszavonta a hozzáférést (pl. jelszócsere után). Kapcsold össze újra.
            </p>
          ) : (
            <p className="text-ink-muted">
              Összekötve.{' '}
              {data.syncedAt
                ? `Utoljára frissítve: ${new Date(data.syncedAt).toLocaleTimeString('hu-HU', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}.`
                : 'Az első beolvasás folyamatban…'}
              {data.error ? ` Hiba: ${data.error}` : ''}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            {data.needsReauth ? (
              <button
                type="button"
                onClick={startOutlookLink}
                className="rounded-control bg-accent px-2.5 py-1 font-semibold text-accent-ink"
              >
                Újra összekötés
              </button>
            ) : (
              <button
                type="button"
                disabled={syncing}
                onClick={async () => {
                  setSyncing(true);
                  try {
                    await syncOutlookNow();
                  } finally {
                    setSyncing(false);
                    await refresh();
                    await queryClient.invalidateQueries({ queryKey: ['calendar'] });
                  }
                }}
                className="rounded-control border border-border px-2.5 py-1 text-ink-muted hover:text-ink disabled:opacity-60"
              >
                {syncing ? 'Frissítés…' : 'Frissítés most'}
              </button>
            )}
            <button
              type="button"
              onClick={async () => {
                const ok = await confirm({
                  title: 'Outlook-naptár leválasztása',
                  body: 'A beolvasott megbeszélések eltűnnek az AVSlackből, és a „Megbeszélésen” státusz sem jelenik meg többé.',
                  confirmLabel: 'Leválasztás',
                  danger: true,
                });
                if (!ok) return;
                await unlinkOutlook();
                await refresh();
                await queryClient.invalidateQueries({ queryKey: ['calendar'] });
              }}
              className="rounded-control border border-border px-2.5 py-1 text-ink-muted hover:text-ink"
            >
              Leválasztás
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
